<template>
  <div class="overflow-x-auto bg-white rounded-lg shadow-sm border border-gray-200">
    <table class="min-w-full divide-y divide-gray-200">
      <thead class="bg-gray-50">
        <tr>
          <th
            scope="col"
            class="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide"
          >
            Name
          </th>
          <th
            scope="col"
            class="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide"
          >
            {{ usersColumnLabel }}
          </th>
          <th
            scope="col"
            class="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide"
          >
            Guide
          </th>
        </tr>
      </thead>
      <tbody class="bg-white divide-y divide-gray-200">
        <tr v-if="!items.length">
          <td colspan="3" class="px-6 py-8 text-center text-sm text-gray-500">
            {{ emptyMessage }}
          </td>
        </tr>

        <tr
          v-for="item in items"
          :key="getRowId(item)"
          @click="$emit('row-click', item)"
          class="hover:bg-gray-50 transition-colors duration-150 cursor-pointer"
        >
          <!-- Name Column -->
          <td class="px-6 py-4 whitespace-nowrap align-middle">
            <div class="max-w-xs flex flex-col justify-between">
              <div class="flex items-center gap-2">
                <h2 class="text-lg text-gray-900 truncate">
                  {{ getDisplayName(item) }}
                </h2>
                <span
                  v-if="getBadge(item)"
                  :class="getBadge(item)!.class"
                  class="inline-flex items-center text-sm leading-5 rounded-full"
                >
                  {{ getBadge(item)!.text }}
                </span>
              </div>
              <div class="flex text-gray-400 flex-wrap max-w-xs mt-2 space-x-2">
                <div class="flex items-center gap-1 text-sm">
                  <Users class="w-3.5 h-3.5" />
                  <span>{{ getUsers(item).length }}</span>
                </div>
                <span class="text-sm">•</span>
                <div class="text-sm">
                  Start date: {{ formatDate(getStartDate(item)) }}
                </div>
              </div>
              <div
                v-if="getMeetingTime && getMeetingTime(item)"
                class="flex items-center gap-1 text-gray-400 text-sm mt-0.5"
              >
                <Clock class="w-3.5 h-3.5" />
                <span>{{ getMeetingTime(item) }}</span>
              </div>
            </div>
          </td>

          <!-- Users Column -->
          <td class="px-6 py-4 whitespace-nowrap align-middle">
            <div class="flex -space-x-3">
              <template v-for="user in getUsers(item)" :key="user.id">
                <div
                  v-if="user.avatar_image"
                  class="inline-block h-16 w-16 rounded-full ring-2 ring-white overflow-hidden"
                >
                  <img
                    class="h-full w-full object-cover"
                    :class="{ 'blur-md select-none': isBlurred }"
                    :src="user.avatar_image"
                    :alt="`${user.first_name} ${user.last_name}`"
                  />
                </div>
                <span
                  v-else
                  class="inline-flex items-center justify-center h-16 w-16 rounded-full ring-2 ring-white bg-gray-200 overflow-hidden"
                >
                  <span
                    class="inline-flex items-center justify-center h-full w-full"
                    :class="{ 'blur-md select-none': isBlurred }"
                  >
                    <User class="h-5 w-5 text-gray-500" />
                  </span>
                </span>
              </template>
            </div>
          </td>

          <!-- Guide Column -->
          <td class="px-6 py-4 whitespace-nowrap align-middle">
            <template v-if="getGuide(item)">
              <component
                :is="isBlurred ? 'div' : 'router-link'"
                @click.stop
                :to="isBlurred ? undefined : `/profile/${getGuide(item)!.username}`"
                class="relative inline-block"
              >
                <div
                  v-if="getGuide(item)!.avatar_image"
                  class="inline-block h-16 w-16 rounded-full ring-2 ring-white hover:scale-105 transition-all duration-200 overflow-hidden"
                >
                  <img
                    class="h-full w-full object-cover"
                    :class="{ 'blur-md select-none': isBlurred }"
                    :src="getGuide(item)!.avatar_image"
                    :alt="`${getGuide(item)!.first_name} ${getGuide(item)!.last_name}`"
                  />
                </div>
                <span
                  v-else
                  class="inline-flex items-center justify-center h-16 w-16 rounded-full ring-2 ring-white bg-gray-200 overflow-hidden"
                >
                  <span
                    class="inline-flex items-center justify-center h-full w-full"
                    :class="{ 'blur-md select-none': isBlurred }"
                  >
                    <User class="h-6 w-6 text-gray-500" />
                  </span>
                </span>

                <!-- Employer badges -->
                <div
                  v-if="isAdmin && getMainGuideEmployers(getGuide(item)!.email).length"
                  class="absolute -bottom-2 -right-2"
                >
                  <template v-if="getMainGuideEmployers(getGuide(item)!.email).length <= 2">
                    <div class="flex -space-x-1">
                      <img
                        v-for="employer in getMainGuideEmployers(getGuide(item)!.email)"
                        :key="employer.name"
                        :src="employer.logo"
                        :alt="employer.name"
                        class="w-6 h-6 rounded-full border-2 border-white bg-white object-contain hover:scale-110 transition-transform duration-150"
                        :title="employer.name"
                      />
                    </div>
                  </template>
                  <template v-else>
                    <div class="relative translate-x-1 translate-y-1">
                      <div class="flex justify-center -mb-1">
                        <img
                          :src="getMainGuideEmployers(getGuide(item)!.email)[0].logo"
                          :alt="getMainGuideEmployers(getGuide(item)!.email)[0].name"
                          class="w-6 h-6 rounded-full border-2 border-white bg-white object-contain hover:scale-110 transition-transform duration-150"
                          :title="getMainGuideEmployers(getGuide(item)!.email)[0].name"
                        />
                      </div>
                      <div class="flex -space-x-1">
                        <img
                          v-for="employer in getMainGuideEmployers(getGuide(item)!.email).slice(1, 3)"
                          :key="employer.name"
                          :src="employer.logo"
                          :alt="employer.name"
                          class="w-6 h-6 rounded-full border-2 border-white bg-white object-contain hover:scale-110 transition-transform duration-150"
                          :title="employer.name"
                        />
                      </div>
                    </div>
                  </template>
                </div>
              </component>
            </template>
            <span v-else class="text-sm text-gray-400">—</span>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script setup lang="ts">
import { Clock, User, Users } from 'lucide-vue-next'
import { formatDate } from '@/utils/formatDate'
import { useAuth } from '@/composables/useAuth'
import { usePrivacyMode } from '@/composables/usePrivacyMode'
import { useMainGuideEmployers } from '@/composables/useMainGuideEmployers'
import type { User as UserType } from '@/types/user'

const props = withDefaults(
  defineProps<{
    items: unknown[]
    usersColumnLabel?: string
    emptyMessage?: string
    getRowId: (item: unknown) => string | number
    getRowUrl: (item: unknown) => string
    getDisplayName: (item: unknown) => string
    getBadge: (item: unknown) => { text: string; class: string } | null
    getUsers: (item: unknown) => UserType[]
    getGuide: (item: unknown) => UserType | null
    getStartDate: (item: unknown) => string
    getMeetingTime?: (item: unknown) => string | null
  }>(),
  {
    usersColumnLabel: 'Adventurers',
    emptyMessage: 'No items found.',
  }
)

defineEmits<{
  'row-click': [item: unknown]
}>()

const { isAdmin } = useAuth()
const { isBlurred } = usePrivacyMode()
const { getMainGuideEmployers } = useMainGuideEmployers()
</script>
